
import React from 'react';
import { Input } from '@/components/ui/input';
import { Clock } from 'lucide-react';

interface PitchTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  videoLength?: number;
}

export function PitchTimePicker({ value, onChange, videoLength = 0 }: PitchTimePickerProps) {
  const handleTimeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Se já contém ":", permitir edição manual
    if (inputValue.includes(':')) {
      const parts = inputValue.split(':');
      if (parts.length === 2) {
        const mins = parseInt(parts[0], 10) || 0;
        const secs = parseInt(parts[1], 10) || 0;

        // Validar segundos
        if (secs > 59) {
          return; // Não permitir
        }

        // Validar contra duração do vídeo
        const totalSeconds = (mins * 60) + secs;
        if (videoLength && videoLength > 0 && totalSeconds > videoLength) {
          return; // Não permitir
        }

        onChange(inputValue);
      }
      return;
    }

    // Extrair apenas números
    let numbersOnly = inputValue.replace(/\D/g, '');

    // Limitar a 4 dígitos
    if (numbersOnly.length > 4) {
      numbersOnly = numbersOnly.substring(0, 4);
    }

    // Se vazio, resetar
    if (numbersOnly.length === 0) {
      onChange('00:00');
      return;
    }

    // Se tem 4 dígitos, formatar como MM:SS
    if (numbersOnly.length === 4) {
      const mins = parseInt(numbersOnly.substring(0, 2), 10);
      let secs = parseInt(numbersOnly.substring(2), 10);

      // Validar segundos
      if (secs > 59) {
        secs = 59;
      }

      // Validar contra duração do vídeo
      const totalSeconds = (mins * 60) + secs;
      if (videoLength && videoLength > 0 && totalSeconds > videoLength) {
        const maxMins = Math.floor(videoLength / 60);
        const maxSecs = videoLength % 60;
        onChange(`${maxMins.toString().padStart(2, '0')}:${maxSecs.toString().padStart(2, '0')}`);
        return;
      }

      onChange(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
    } else {
      // Enquanto digita, apenas mostrar os números
      onChange(numbersOnly);
    }
  };

  return (
    <div className="relative w-full">
      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#AAB3C6] opacity-70 pointer-events-none" />
      <Input
        type="text"
        value={value}
        onChange={handleTimeInput}
        className="pl-10 h-[44px] border-[#DFE7FF] rounded-[12px] text-[#2C3345] font-medium bg-white
          focus:border-[#4B7BFF] focus:ring-0 focus:shadow-[0_0_0_3px_rgba(75,123,255,0.18)]
          placeholder:text-[#AEB8C9] transition-all duration-200"
        placeholder="00:00"
        maxLength={5}
        inputMode="numeric"
      />
    </div>
  );
}
